<script setup>
import { useContactOptions } from '~/composables/useContactOptions'
import { useZendeskChat } from '~/composables/useZendeskChat'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  ignoreElements: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['close'])

const { t } = useI18n()
const { contactOptions } = useContactOptions()
const { openChat } = useZendeskChat()

const panelRef = ref(null)

onClickOutside(panelRef, () => {
  if (props.show) {
    emit('close')
  }
}, {
  ignore: () => props.ignoreElements,
})

function handleChatClick(event) {
  event?.preventDefault()
  event?.stopPropagation()
  openChat(() => emit('close'))
}
</script>

<template>
  <Teleport to="body">
    <Transition name="contact-popover">
      <div
        v-if="show"
        class="contact-popover"
        @click.self="emit('close')"
      >
        <div
          ref="panelRef"
          class="contact-popover__panel"
          @click.stop
        >
          <h2 class="scale-1 scale-1--bold-uppercase contact-popover__title">
            {{ t('contact.popover.title') }}
          </h2>
          <p class="equal-small-2 contact-popover__description">
            {{ t('contact.popover.description') }}
          </p>
          <div class="contact-popover__grid">
            <component
              :is="option.action === 'chat' ? 'button' : 'a'"
              v-for="option in contactOptions"
              :key="option.icon"
              :href="option.action === 'chat' ? undefined : option.href"
              :target="option.target"
              :rel="option.rel"
              :type="option.action === 'chat' ? 'button' : undefined"
              class="contact-popover__option"
              @click="option.action === 'chat' ? handleChatClick($event) : undefined"
            >
              <div class="contact-popover__icon">
                <IconsPhone v-if="option.icon === 'phone'" class="contact-popover__icon-phone" />
                <IconsMail v-else-if="option.icon === 'mail'" class="contact-popover__icon-mail" />
                <IconsWhatsapp v-else-if="option.icon === 'whatsapp'" class="contact-popover__icon-whatsapp" />
                <IconsChat v-else-if="option.icon === 'chat'" class="contact-popover__icon-chat" />
              </div>
              <div class="contact-popover__value-container">
                <span class="equal-small-3 contact-popover__value">{{ option.value }}</span>
                <span class="equal-base contact-popover__label">{{ option.label }}</span>
              </div>
            </component>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/variables.scss';
@import 'bootstrap/scss/mixins';

.contact-popover {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background-color: rgba(0, 0, 0, 0.65);

  @include media-breakpoint-down(880) {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 20px 20px 100px;
  }

  &__panel {
    position: fixed;
    top: 75px;
    right: 25px;
    width: 380px;
    max-width: calc(100vw - 50px);
    padding: 24px;
    background-color: $color-dark-02;
    border: 1px solid $color-dark-01;
    border-radius: 24px;
    color: $color-light;

    @include media-breakpoint-down(880) {
      position: relative;
      top: auto;
      right: auto;
      width: 100%;
    }
  }

  &__title {
    margin: 0 0 12px;
  }

  &__description {
    margin: 0 0 20px;
    color: $color-light;
    line-height: 1.4;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  &__option {
    display: flex;
    flex-direction: column;
    padding: 16px;
    border: 1px solid $color-dark-01;
    border-radius: 12px;
    color: $color-light;
    text-decoration: none;
    transition: background-color 0.2s ease;
    height: 100%;
    justify-content: space-between;

    &:hover {
      background-color: $color-dark-04;
      color: $color-light;
    }
  }

  button.contact-popover__option {
    width: 100%;
    text-align: left;
    border: 1px solid $color-dark-01;
    background-color: transparent;
    cursor: pointer;
    font: inherit;

    &:hover,
    &:focus-visible {
      background-color: $color-dark-04;
      color: $color-light;
    }
  }

  &__icon {
    fill: $color-light;

    &-phone {
      height: 22px;
      width: 22px;
    }

    &-whatsapp {
      height: 26px;
      width: 26px;
    }

    &-mail {
      height: 17px;
      width: 24px;
    }

    &-chat {
      height: 22px;
      width: 21px;
    }
  }

  &__value-container {
    display: flex;
    flex-direction: column;
    margin-top: 16px;
  }

  &__value {
    color: $color-plain-grey-60;
    line-height: 1.3;
    word-break: break-word;
  }

  &__label {
    font-weight: 700;
  }
}

.contact-popover-enter-active,
.contact-popover-leave-active {
  transition: opacity 0.2s ease;

  .contact-popover__panel {
    transition: transform 0.2s ease;
  }
}

.contact-popover-enter-from,
.contact-popover-leave-to {
  opacity: 0;

  .contact-popover__panel {
    transform: translateY(-8px);

    @include media-breakpoint-down(880) {
      transform: translateY(16px);
    }
  }
}
</style>
